import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BookOpen, Search, X, ChevronDown, CheckCircle2, Link2, Check,
  ArrowRight, Sparkles, Filter, ListFilter,
} from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import {
  LEXIQUE,
  LEXIQUE_CATEGORIES,
  searchLexique,
  countByCategory,
  getCategory,
} from '../data/lexique';
import AudienceContextBanner from '../components/AudienceContextBanner';

/**
 * Page /lexique — Lexique pédagogique du motard.
 *
 * • Recherche live (term + définition + keywords, accents-insensitive)
 * • Filtres catégories (chips toggle)
 * • Deep-link via hash (#csg, #point-mort, #code-78…)
 * • Bouton "copier le lien" sur chaque entrée pour partage
 * • Mode "tout déplier / replier"
 * • Animations d'apparition staggered
 * • SEO complet : title/description, JSON-LD DefinedTermSet
 */

// ────────────────────────────────────────────────────────────────────────────
// Highlight helper — surligne le terme recherché dans un texte
// ────────────────────────────────────────────────────────────────────────────
const Highlight = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const q = norm(query);
  const haystack = norm(text);
  const idx = haystack.indexOf(q);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[hsl(var(--primary)/0.2)] text-foreground px-0.5 rounded-sm">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// LexiqueCard — carte d'un terme, dépliable, avec deep-link
// ────────────────────────────────────────────────────────────────────────────
const LexiqueCard = ({ entry, query, expanded, onToggle, focused }) => {
  const Icon = entry.icon;
  const cat = getCategory(entry.category);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  // Scroll vers la carte si "focused" (deep-link)
  useEffect(() => {
    if (focused && ref.current) {
      const t = setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [focused]);

  const copyLink = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/lexique#${entry.slug}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div
      ref={ref}
      id={entry.slug}
      data-testid={`lexique-card-${entry.slug}`}
      className={cn(
        'mc-card p-5 relative overflow-hidden group transition-all duration-200',
        'hover:shadow-lg hover:-translate-y-0.5',
        focused && 'ring-2 ring-offset-2 ring-offset-background shadow-xl',
        expanded && 'shadow-md',
      )}
      style={focused ? { '--tw-ring-color': entry.color } : undefined}
    >
      {/* halo coloré décoratif */}
      <div
        aria-hidden="true"
        className="absolute -top-12 -right-12 w-28 h-28 rounded-full blur-2xl opacity-15 transition-opacity group-hover:opacity-25"
        style={{ background: entry.color }}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full text-left flex items-start gap-3 relative"
        data-testid={`lexique-toggle-${entry.slug}`}
      >
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: entry.soft, color: entry.color }}
        >
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <h3 className="text-base sm:text-lg font-black leading-tight">
              <Highlight text={entry.term} query={query} />
            </h3>
            <span
              className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
              style={{ background: cat?.soft || '#f3f4f6', color: cat?.color || '#374151' }}
            >
              {cat?.short || 'Lexique'}
            </span>
          </div>
          <p className={cn(
            'text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed',
            !expanded && 'line-clamp-2',
          )}>
            <Highlight text={entry.definition} query={query} />
          </p>
        </div>

        <ChevronDown
          className={cn(
            'w-4 h-4 mt-2 flex-shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {/* Contenu déplié */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-border/60 relative animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
            Règles & repères
          </div>
          <ul className="space-y-2 mb-3">
            {entry.rules.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2
                  className="w-3.5 h-3.5 flex-shrink-0 mt-1"
                  style={{ color: entry.color }}
                  aria-hidden="true"
                />
                <span className="leading-relaxed text-foreground/85">
                  <Highlight text={r} query={query} />
                </span>
              </li>
            ))}
          </ul>

          {entry.distinction && (
            <div
              className="rounded-lg p-3 text-xs sm:text-[13px] leading-relaxed flex items-start gap-2 mb-3"
              style={{ background: `${entry.color}10`, borderLeft: `3px solid ${entry.color}` }}
            >
              <span
                className="font-black uppercase tracking-widest text-[10px] flex-shrink-0 mt-0.5"
                style={{ color: entry.color }}
              >
                Distinction
              </span>
              <span className="text-foreground/85">
                <Highlight text={entry.distinction} query={query} />
              </span>
            </div>
          )}

          {/* Footer card : copier le lien */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={copyLink}
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 transition-all',
                'border border-border hover:border-foreground/30 hover:bg-muted/40',
                copied && 'bg-emerald-50 border-emerald-300 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300',
              )}
              data-testid={`lexique-copy-${entry.slug}`}
              aria-label="Copier le lien direct vers ce terme"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  Lien copié
                </>
              ) : (
                <>
                  <Link2 className="w-3 h-3" />
                  Copier le lien
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// PAGE COMPONENT
// ────────────────────────────────────────────────────────────────────────────
export default function Lexique() {
  const location = useLocation();
  const initialHash = (typeof window !== 'undefined' ? window.location.hash : '').replace('#', '');

  const [query, setQuery] = useState('');
  const [activeCats, setActiveCats] = useState([]); // [] = all
  const [expanded, setExpanded] = useState(() => {
    // Pré-déplier l'entrée du deep-link s'il y en a un
    return initialHash ? new Set([initialHash]) : new Set();
  });
  const [focusedSlug, setFocusedSlug] = useState(initialHash || null);

  // Liste filtrée
  const results = useMemo(() => {
    let list = searchLexique(query);
    if (activeCats.length > 0) {
      list = list.filter((e) => activeCats.includes(e.category));
    }
    return list;
  }, [query, activeCats]);

  const counts = useMemo(() => countByCategory(), []);

  // SEO + JSON-LD DefinedTermSet
  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'Lexique du motard — Code 2 Roues',
    description: `${LEXIQUE.length} définitions officielles : CSG, P-A-S, CIF, point mort, cyclistes, formation 7h, passerelle A1→A2, mention 78 (TMAX), trajectoire, équipements et bien plus.`,
    inLanguage: 'fr-FR',
    hasDefinedTerm: LEXIQUE.map((e) => ({
      '@type': 'DefinedTerm',
      '@id': `https://code2roues.fr/lexique#${e.slug}`,
      name: e.term,
      description: e.definition,
      termCode: e.slug,
      inDefinedTermSet: 'https://code2roues.fr/lexique',
    })),
  }), []);

  useSeo({
    title: `Lexique Code Moto 2026 — ${LEXIQUE.length} termes ETM`,
    description: `${LEXIQUE.length} termes clés du Code Moto 2026 expliqués : CSG, P-A-S, CIF, point mort, formation 7h, passerelle A1→A2, mention 78, trajectoire virage…`,
    keywords: 'lexique moto, définitions code moto, CSG, P-A-S premiers secours, circulation inter-files CIF, point mort moto, cyclistes vulnérables, formation 7h, passerelle A1 A2, mention 78 TMAX, trajectoire virage moto',
    path: '/lexique',
    jsonLd,
  });

  // Met à jour le focus quand le hash change (navigation interne)
  useEffect(() => {
    const handleHash = () => {
      const h = window.location.hash.replace('#', '');
      if (h) {
        setFocusedSlug(h);
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(h);
          return next;
        });
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  // Helpers
  const toggleEntry = (slug) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleCategory = (id) => {
    setActiveCats((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const expandAll = () => setExpanded(new Set(results.map((e) => e.slug)));
  const collapseAll = () => setExpanded(new Set());

  const clearFilters = () => {
    setQuery('');
    setActiveCats([]);
  };

  const hasActiveFilters = query.length > 0 || activeCats.length > 0;
  const allExpanded = results.length > 0 && results.every((e) => expanded.has(e.slug));

  return (
    <div className="space-y-5 sm:space-y-8 py-2" data-testid="page-lexique">
      {/* ─────── HERO ─────── */}
      <section className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-border bg-gradient-to-br from-[hsl(var(--primary)/0.08)] via-card to-card p-4 sm:p-8">
        <div
          aria-hidden="true"
          className="absolute -top-20 -right-20 w-44 sm:w-56 h-44 sm:h-56 rounded-full blur-3xl opacity-25 bg-[hsl(var(--primary))]"
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1.5 sm:mb-2">
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="hidden sm:inline">Vocabulaire ECSR · Conforme 2026</span>
              <span className="sm:hidden">ECSR · 2026</span>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
              Le lexique<br className="hidden sm:inline" /> du motard.
            </h1>
            {/* Description : 2 lignes max sur mobile (line-clamp), full sur desktop */}
            <p className="mt-2 sm:mt-3 text-[13px] sm:text-base text-foreground/75 max-w-2xl leading-relaxed line-clamp-3 sm:line-clamp-none">
              <strong className="text-foreground">{LEXIQUE.length} termes essentiels</strong> du code de la route moto avec règles, nuances et pièges à éviter.
              <span className="hidden sm:inline">{' '}Cherchez un mot pendant la lecture d'un dossier ou avant une leçon — partout où vous en avez besoin.</span>
            </p>

            {/* Stats inline mobile (compact), full cards desktop */}
            <div className="flex items-center gap-3 mt-3 sm:hidden text-xs font-bold" data-testid="lexique-stats-mobile">
              <span className="inline-flex items-center gap-1">
                <span className="text-base font-black text-[hsl(var(--primary))]">{LEXIQUE.length}</span>
                <span className="text-muted-foreground uppercase tracking-wider text-[10px]">termes</span>
              </span>
              <span className="w-px h-4 bg-border" />
              <span className="inline-flex items-center gap-1">
                <span className="text-base font-black text-[hsl(var(--primary))]">{LEXIQUE_CATEGORIES.length}</span>
                <span className="text-muted-foreground uppercase tracking-wider text-[10px]">catégories</span>
              </span>
            </div>
          </div>

          {/* Stats desktop (cards) */}
          <div className="hidden sm:flex gap-3 flex-shrink-0" data-testid="lexique-stats">
            <div className="flex flex-col items-center justify-center px-4 py-3 rounded-2xl bg-card border border-border min-w-[88px]">
              <span className="text-3xl font-black text-[hsl(var(--primary))] leading-none">{LEXIQUE.length}</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">termes</span>
            </div>
            <div className="flex flex-col items-center justify-center px-4 py-3 rounded-2xl bg-card border border-border min-w-[88px]">
              <span className="text-3xl font-black text-[hsl(var(--primary))] leading-none">{LEXIQUE_CATEGORIES.length}</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">catégories</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau d'adaptation par audience (visible pour les 3 publics) */}
      <AudienceContextBanner
        pageHint="Le lexique du motard sert tout le monde : apprendre, enseigner, citer face au jury."
        hideOnEmpty
        contexts={{
          eleve: {
            title: 'Comprendre les mots du code de la route',
            bullets: [
              'Cherche un mot pendant la lecture d\'un dossier ou avant une leçon.',
              'Astuces visuelles + pièges à éviter pour mémoriser durablement.',
            ],
          },
          enseignant: {
            title: 'Vocabulaire à transmettre à tes élèves',
            bullets: [
              'Vérifie le sens exact avant d\'expliquer en cours (CSG, point mort, code 78, etc.).',
              'Copie le lien d\'un terme pour le partager en session.',
            ],
            cta: 'Voir le canevas pédagogique',
            ctaTo: '/enseignant/canevas-pedagogique',
          },
          'futur-enseignant': {
            title: 'Maîtriser le vocabulaire ECSR pour l\'oral CCS',
            bullets: [
              'Cite les termes officiels (REMC, GDE, PCRB) sans confusion devant le jury.',
              'Repère les nuances clés : démarrage ≠ décollage, accident ≠ presqu\'accident.',
            ],
            cta: 'Voir les fondements pédagogiques',
            ctaTo: '/enseignant/fondements-pedagogie',
          },
        }}
      />

      {/* ─────── BARRE DE RECHERCHE + FILTRES ─────── */}
      <section
        className="space-y-3 sm:space-y-4 sticky top-[56px] sm:top-[64px] z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2.5 sm:py-3 bg-background/90 backdrop-blur-md border-y border-border"
        data-testid="lexique-search-bar"
      >
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          {/* Placeholder court mobile / long desktop pour éviter la troncature */}
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher : CSG, point mort, code 78…"
            className="pl-11 pr-11 h-11 sm:h-12 text-sm sm:text-base rounded-2xl shadow-sm sm:hidden"
            aria-label="Rechercher dans le lexique"
            data-testid="lexique-search-input-mobile"
          />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un terme : CSG, point mort, code 78, P-A-S, cycliste…"
            className="pl-11 pr-11 h-12 text-base rounded-2xl shadow-sm hidden sm:block"
            aria-label="Rechercher dans le lexique"
            data-testid="lexique-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Effacer la recherche"
              data-testid="lexique-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Chips catégories — horizontalement scrollables sur mobile, wrap sur desktop */}
        <div
          className="flex items-center gap-1.5 overflow-x-auto sm:flex-wrap -mx-1 px-1 pb-0.5 sm:overflow-visible"
          style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          <div className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground mr-1">
            <Filter className="w-3 h-3" />
            <span>Catégories</span>
          </div>
          {LEXIQUE_CATEGORIES.map((c) => {
            const active = activeCats.includes(c.id);
            const count = counts[c.id] || 0;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.id)}
                className={cn(
                  'flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border whitespace-nowrap',
                  active
                    ? 'shadow-sm scale-[1.02]'
                    : 'border-border bg-card hover:bg-muted/40 text-foreground/75 hover:text-foreground',
                )}
                style={active ? {
                  background: c.soft,
                  color: c.color,
                  borderColor: c.color,
                } : undefined}
                data-testid={`lexique-filter-${c.id}`}
                aria-pressed={active}
              >
                <span>{c.short}</span>
                <span className={cn(
                  'text-[10px] font-black px-1.5 py-0.5 rounded-full',
                  active ? 'bg-white/40 dark:bg-black/20' : 'bg-muted',
                )}>
                  {count}
                </span>
              </button>
            );
          })}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="flex-shrink-0 ml-auto text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-muted/40 whitespace-nowrap"
              data-testid="lexique-clear-filters"
            >
              <X className="w-3 h-3" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Compteur résultats + tout déplier */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs font-semibold text-muted-foreground" data-testid="lexique-results-count">
            {results.length === LEXIQUE.length ? (
              <>{LEXIQUE.length} termes au total</>
            ) : (
              <>
                <span className="text-foreground font-black">{results.length}</span>{' '}
                {results.length > 1 ? 'termes correspondent' : 'terme correspond'}
                {hasActiveFilters && results.length < LEXIQUE.length && (
                  <span className="text-foreground/60"> · {LEXIQUE.length - results.length} masqués</span>
                )}
              </>
            )}
          </div>
          {results.length > 0 && (
            <button
              type="button"
              onClick={allExpanded ? collapseAll : expandAll}
              className="text-xs font-semibold text-foreground/75 hover:text-foreground inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full hover:bg-muted/40 border border-border/60"
              data-testid="lexique-toggle-all"
            >
              <ListFilter className="w-3 h-3" />
              {allExpanded ? 'Tout replier' : 'Tout déplier'}
            </button>
          )}
        </div>
      </section>

      {/* ─────── GRID DE CARDS ─────── */}
      {results.length > 0 ? (
        <section className="grid md:grid-cols-2 gap-4" data-testid="lexique-grid">
          {results.map((entry) => (
            <LexiqueCard
              key={entry.slug}
              entry={entry}
              query={query}
              expanded={expanded.has(entry.slug)}
              onToggle={() => toggleEntry(entry.slug)}
              focused={focusedSlug === entry.slug}
            />
          ))}
        </section>
      ) : (
        <section
          className="mc-card p-10 sm:p-14 text-center max-w-2xl mx-auto"
          data-testid="lexique-empty-state"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-muted flex items-center justify-center mb-4">
            <Search className="w-6 h-6 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-black tracking-tight">Aucun terme ne correspond.</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Essayez avec un mot-clé différent (par ex. <em>« giratoire »</em>, <em>« 78 »</em>,{' '}
            <em>« point mort »</em>) ou réinitialisez les filtres.
          </p>
          <Button
            onClick={clearFilters}
            className="mt-5 mc-btn-primary rounded-full"
            data-testid="lexique-empty-reset"
          >
            Réinitialiser la recherche
          </Button>
        </section>
      )}

      {/* ─────── CTA / Liens contextuels (3 cards : compactes en mobile, larges en desktop) ─────── */}
      <section className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="lexique-related">
        <Link
          to="/programme-etm"
          className="mc-card p-2.5 sm:p-4 group hover:shadow-lg transition-all flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-3 text-center sm:text-left"
          data-testid="lexique-link-programme"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[hsl(var(--primary)/0.1)] flex items-center justify-center text-[hsl(var(--primary))] flex-shrink-0">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-muted-foreground hidden sm:block">Référentiel</div>
            <div className="text-[11px] sm:text-sm font-black leading-tight sm:truncate">Programme ETM</div>
          </div>
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0 hidden sm:block" />
        </Link>
        <Link
          to="/revision"
          className="mc-card p-2.5 sm:p-4 group hover:shadow-lg transition-all flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-3 text-center sm:text-left"
          data-testid="lexique-link-revision"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[hsl(var(--primary)/0.1)] flex items-center justify-center text-[hsl(var(--primary))] flex-shrink-0">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-muted-foreground hidden sm:block">S'entraîner</div>
            <div className="text-[11px] sm:text-sm font-black leading-tight sm:truncate">Révision</div>
          </div>
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0 hidden sm:block" />
        </Link>
        <Link
          to="/revision"
          className="mc-card p-2.5 sm:p-4 group hover:shadow-lg transition-all flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-3 text-center sm:text-left"
          data-testid="lexique-link-revision"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[hsl(var(--primary)/0.1)] flex items-center justify-center text-[hsl(var(--primary))] flex-shrink-0">
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-muted-foreground hidden sm:block">9 thèmes</div>
            <div className="text-[11px] sm:text-sm font-black leading-tight sm:truncate">Apprentissage</div>
          </div>
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0 hidden sm:block" />
        </Link>
      </section>
    </div>
  );
}
